/**
 * Help Screen
 * 
 * Help page with FAQs and feedback options.
 * Matches iOS HelpView.swift implementation.
 * 
 * @module components/menu/HelpScreen
 */

import React from 'react';
import { MFELoader } from '@/components/mfe/MFELoader';
import { useAuth } from '@/contexts/AuthContext';
import { CommunicationBridge } from '@sequelae/lore-mfe-kit';
import { Button } from '@sequelae/lore-core';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import './HelpScreen.css';

interface HelpMenuItemProps {
  title: string;
  icon: React.ReactNode;
  onPress: () => void;
}

const HelpMenuItem: React.FC<HelpMenuItemProps> = ({ title, icon, onPress }) => (
  <button className="help-menu-item" onClick={onPress}>
    <span className="help-menu-icon">{icon}</span>
    <span className="help-menu-title">{title}</span>
    <ChevronRightIcon className="help-menu-arrow" />
  </button>
);

interface HelpScreenProps {
  currentPage: 'help' | 'help-faqs' | 'help-feedback';
  onNavigate: (page: string) => void;
}

export const HelpScreen: React.FC<HelpScreenProps> = ({ currentPage, onNavigate }) => {
  const { user, logout } = useAuth();
  const token = CommunicationBridge.getAccessToken();

  if (!token || !user) {
    return <div>Unauthorized</div>;
  }

  // Show FAQs MFE
  if (currentPage === 'help-faqs') {
    return (
      <div className="help-mfe-wrapper">
        <div className="help-mfe-container">
          <MFELoader
            mfeName="FAQ"
            accessToken={token}
            refUserId={user.id}
            onLogout={logout}
          />
        </div>
      </div>
    );
  }

  // Show Feedback form
  if (currentPage === 'help-feedback') {
    return (
      <div className="help-feedback-container">
        <h3>Send us a message</h3>
        <p>Have a question or feedback? We'd love to hear from you!</p>
        <Button
          onClick={() => {
            window.location.href = 'mailto:hello@lore.co?subject=Lore%20Web%20App%20Feedback';
          }}
          variant="primary"
          size="large"
          fullWidth
        >
          Send Email
        </Button>
        <p className="help-email-note">
          Or email us directly at <strong>hello@lore.co</strong>
        </p>
      </div>
    );
  }

  // Main help menu
  return (
    <div className="help-container">
      <div className="help-menu-group">
        <HelpMenuItem
          title="FAQs"
          icon={<HelpOutlineIcon />}
          onPress={() => onNavigate('help-faqs')}
        />
        <HelpMenuItem
          title="Send us a message"
          icon={<EmailOutlinedIcon />}
          onPress={() => onNavigate('help-feedback')}
        />
      </div>

      {/* Version Info */}
      <div className="help-version">
        <div>Version 1.0.0</div>
        <div>Lore Web Client</div>
        <div className="help-copyright">© 2026 Lore Health</div>
      </div>
    </div>
  );
};

